import { ScheduleEntry } from "@models/ScheduleEntry.model";
import { ScheduleSegment } from "@models/ScheduleSegment.model";

/*
    Geometry and derivation for the schedule list. The whole campaign always
    fits the track width, so every position is a fraction of the campaign's
    span rather than a pixel offset — that is what makes rows line up.
*/

export const DAY_MS = 24 * 60 * 60 * 1000;

export function dayStart(value: Date | string): Date {
  const d = value instanceof Date ? value : new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Inclusive day count — Mar 1 to Mar 1 is one day, not zero. */
export function dayCount(start: Date, end: Date): number {
  return Math.round((dayStart(end).getTime() - dayStart(start).getTime()) / DAY_MS) + 1;
}

export function sameDay(a: Date | string, b: Date | string): boolean {
  return dayStart(a).getTime() === dayStart(b).getTime();
}

export interface Range {
  start: Date;
  end: Date;
}

/*
    An entry's flights ARE its segments — nothing else. The backend only serves a
    row while one of its own segments covers now (content_network resolve_status),
    so an entry with no segments is not scheduled and will not serve, whatever
    start_time and end_time say. Those two are the envelope, not the schedule.
*/
export function rangesOf(entry: ScheduleEntry): Range[] {
  return [...(entry.segments ?? [])]
    .map((s) => ({ start: dayStart(s.start_time), end: dayStart(s.end_time) }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}

/*
    "Follows the parent" is not stored, it is derived: a single flight covering
    exactly the parent's window. It has to be a real segment — a child does not
    inherit its parent's serving window, it only serves on its own segments.
*/
export function inheritsParent(entry: ScheduleEntry, parent: ScheduleEntry | null): boolean {
  if (!parent) return false;
  const ranges = rangesOf(entry);
  if (ranges.length !== 1) return false;
  return (
    sameDay(entry.start_time, parent.start_time) &&
    sameDay(entry.end_time, parent.end_time) &&
    sameDay(ranges[0].start, parent.start_time) &&
    sameDay(ranges[0].end, parent.end_time)
  );
}

export interface ScheduleRow {
  entry: ScheduleEntry;
  parent: ScheduleEntry | null;
  depth: number;
  /** A platform override modifies the row above it rather than nesting under it. */
  isOverride: boolean;
  inherits: boolean;
  ranges: Range[];
  /** Platform override children, kept off the row list — the modal owns them. */
  overrides: ScheduleEntry[];
}

/*
    Flatten the tree into display rows. Platform overrides (children carrying a
    subtype) are pulled out of the main flow and attached to the row they
    modify, so the list stays one row per real thing.
*/
export function flattenTree(entries: readonly ScheduleEntry[]): ScheduleRow[] {
  const rows: ScheduleRow[] = [];

  const walk = (entry: ScheduleEntry, parent: ScheduleEntry | null, depth: number) => {
    const children = entry.children ?? [];
    const overrides = children.filter((c) => !!c.subtype);
    rows.push({
      entry,
      parent,
      depth,
      isOverride: false,
      inherits: inheritsParent(entry, parent),
      ranges: rangesOf(entry),
      overrides,
    });
    for (const override of overrides) {
      rows.push({
        entry: override,
        parent: entry,
        // Same indent as what it modifies — indenting it would read as a child
        // of that row, which is what ads are.
        depth,
        isOverride: true,
        inherits: inheritsParent(override, entry),
        ranges: rangesOf(override),
        overrides: [],
      });
    }
    for (const child of children.filter((c) => !c.subtype)) {
      walk(child, entry, depth + 1);
    }
  };

  for (const entry of entries) walk(entry, null, 0);
  return rows;
}

export interface Window {
  start: Date;
  end: Date;
  totalDays: number;
}

/*
    The campaign window every track is drawn against. Widened to cover anything
    that escapes it, so a child that outruns its parent is still visible rather
    than clipped off the end.
*/
export function campaignWindow(rows: readonly ScheduleRow[], root: ScheduleEntry | null): Window {
  const dates: Date[] = [];
  if (root) {
    dates.push(dayStart(root.start_time), dayStart(root.end_time));
  }
  for (const row of rows) {
    for (const range of row.ranges) dates.push(range.start, range.end);
  }
  if (dates.length === 0) {
    const today = dayStart(new Date());
    return { start: today, end: today, totalDays: 1 };
  }
  const start = new Date(Math.min(...dates.map((d) => d.getTime())));
  const end = new Date(Math.max(...dates.map((d) => d.getTime())));
  return { start, end, totalDays: dayCount(start, end) };
}

export interface BarGeometry {
  leftPct: number;
  widthPct: number;
}

export function barGeometry(range: Range, window: Window): BarGeometry {
  const offset = Math.round((range.start.getTime() - window.start.getTime()) / DAY_MS);
  const span = dayCount(range.start, range.end);
  return {
    leftPct: (offset / window.totalDays) * 100,
    widthPct: (span / window.totalDays) * 100,
  };
}

export interface AxisTick {
  label: string;
  leftPct: number;
  /** Month starts carry the month name and the heavier gridline. */
  isMonthStart: boolean;
}

// Day-of-month marks inside each month. Regular, and they never collide with
// the 1st, so the axis reads like a calendar instead of an arbitrary interval.
const DAY_MARKS = [8, 15, 22, 29];

// Room a label needs before the next one starts crowding it.
const LABEL_GAP_PX = 40;

/*
    Axis ticks anchored to the calendar, not to the window start: the 1st of
    every month, plus day marks within it when there is room. Anything else
    produces labels like "Jul 15, Aug 5, Sep 2" that agree with nothing.
*/
export function axisTicks(window: Window, dayPx: number): AxisTick[] {
  const ticks: AxisTick[] = [];
  const pctOf = (date: Date) =>
    ((dayCount(window.start, date) - 1) / window.totalDays) * 100;

  const spansYears = window.start.getFullYear() !== window.end.getFullYear();
  const monthLabel = (date: Date) =>
    date.toLocaleDateString(undefined, spansYears ? { month: "short", year: "2-digit" } : { month: "short" });

  // A window starting mid-month still needs its opening date named.
  const startsMidMonth = window.start.getDate() !== 1;
  if (startsMidMonth) {
    ticks.push({
      label: window.start.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      leftPct: 0,
      isMonthStart: true,
    });
  }

  // Below ~8px a day the in-month marks stop being readable, so months only.
  const showDayMarks = dayPx >= 8;

  const cursor = new Date(window.start.getFullYear(), window.start.getMonth(), 1);
  while (cursor <= window.end) {
    const monthStart = new Date(cursor);
    if (monthStart >= window.start) {
      ticks.push({ label: monthLabel(monthStart), leftPct: pctOf(monthStart), isMonthStart: true });
    }

    if (showDayMarks) {
      for (const day of DAY_MARKS) {
        const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), day);
        if (date.getMonth() !== monthStart.getMonth()) continue;
        if (date < window.start || date > window.end) continue;
        ticks.push({ label: String(day), leftPct: pctOf(date), isMonthStart: false });
      }
    }

    cursor.setMonth(cursor.getMonth() + 1);
  }

  ticks.sort((a, b) => a.leftPct - b.leftPct);

  /*
      Thin out anything that would overlap. A window starting mid-month puts the
      opening label and a day mark on the same pixel, and the pinned end date
      needs clear space, so both ends are protected. Month starts win ties.
  */
  const trackPx = dayPx * window.totalDays;
  if (trackPx <= 0) return ticks;
  const minGapPct = (LABEL_GAP_PX / trackPx) * 100;

  const kept: AxisTick[] = [];
  for (const tick of ticks) {
    if (tick.leftPct > 100 - minGapPct) continue; // reserved for the closing date
    const last = kept[kept.length - 1];
    if (last && tick.leftPct - last.leftPct < minGapPct) {
      if (tick.isMonthStart && !last.isMonthStart) kept[kept.length - 1] = tick;
      continue;
    }
    kept.push(tick);
  }
  return kept;
}

/** Month boundaries — the heavier gridlines, matching the named ticks exactly. */
export function monthBoundaries(window: Window): number[] {
  const offsets: number[] = [];
  const cursor = new Date(window.start.getFullYear(), window.start.getMonth() + 1, 1);
  while (cursor <= window.end) {
    offsets.push(((dayCount(window.start, cursor) - 1) / window.totalDays) * 100);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return offsets;
}

export function formatRange(range: Range): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const sameMonth =
    range.start.getMonth() === range.end.getMonth() &&
    range.start.getFullYear() === range.end.getFullYear();
  const end = sameMonth
    ? String(range.end.getDate())
    : range.end.toLocaleDateString(undefined, opts);
  return `${range.start.toLocaleDateString(undefined, opts)} – ${end}`;
}

export function formatRanges(ranges: readonly Range[]): string {
  return ranges.map(formatRange).join(", ");
}

/*
    The dates column is one line wide. Spelling out every flight wrapped it into
    a block that pushed the row out of alignment, so only the first is named and
    the rest are counted — the full list stays available on hover.
*/
export function summarizeRanges(ranges: readonly Range[]): string {
  if (ranges.length === 0) return "—";
  if (ranges.length === 1) return formatRange(ranges[0]);
  return `${formatRange(ranges[0])} +${ranges.length - 1} more`;
}

export function newSegment(entryId: number, start: Date, end: Date): ScheduleSegment {
  const segment = new ScheduleSegment();
  segment.id = -1;
  segment.entry_id = entryId;
  segment.start_time = start;
  segment.end_time = end;
  return segment;
}

/** Label for the level a row sits at, used by the row tag. */
export function typeLabel(entry: ScheduleEntry): string {
  switch (entry.type?.toUpperCase()) {
    case "LAUNCH":
      return "Campaign";
    case "AUDIENCE":
      return "Ad group";
    case "AD":
      return "Ad";
    default:
      return entry.type || "Item";
  }
}
